import * as L from 'leaflet';

import {
  SELECT_MAP_LAYER,
  DESELECT_MAP_LAYER,
  DESELECT_ALL_MAP_LAYERS,
  TOGGLE_BASEMAP,
  GEOJSON_LAYER_REQUEST,
  GEOJSON_LAYER_SUCCESS,
  GEOJSON_LAYER_ERROR,
  ZOOM_TO_MAP_HOME,
  ZOOM_TO_MAP_LAYER_EXTENT,
} from '../common/actionTypes';

// URL to the S3 bucket where GeoJSON and tiles are stored
import { s3Url, DEFAULT_LAYERS } from '../common/config';

// list of data layers with human readable names mapped to AWS S3 bucket keys
import dataInventory from '../../data/data_inventory.json';

// tile layer xyz
const zxy = '{z}/{x}/{y}.png';

// takes a value like "255, 255, 255" and converts to "rgb(255, 255, 255)"
const parseRgbValue = s => {
  const rgb = s.split(',');
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
};

// create a hash / lookup table of data layers
const dataLayers = dataInventory.reduce((hash, d) => {
  // early return if the layer isn't used in the expert map
  if (!d.expert_map) return hash;
  // store the JSON object as a value of the hash
  hash[d.datacode] = { ...d };
  // if the layer is a tile layer, go ahead and create it, otherwise leave empty bc geojson layer
  if (d.datatype === 'tileLayer') {
    hash[d.datacode].leaflet = L.tileLayer(`${s3Url}/${d.datacode}/${zxy}`, { zIndex: 3 });
  } else if (d.datatype === 'marker') {
    const coords = d.marker_lat_lng.split(',');
    const marker = L.marker(coords);
    // hack: get the marker properties saved on the marker for use elsewhere
    window.ginprop = d;
    marker.properties = d;
    hash[d.datacode].leaflet = marker;
  } else {
    hash[d.datacode].leaflet = null;
  }

  // add a flag to determine if the layer has been selected or not
  hash[d.datacode].selected = DEFAULT_LAYERS.indexOf(hash[d.datacode].datacode) !== -1;
  // if it's a geojson layer, create a style property from attributes
  hash[d.datacode].style =
    d.datatype === 'geojson'
      ? {
        color: parseRgbValue(d.stroke_color),
        weight: +d.stroke_weight,
        opacity: +d.stroke_opacity,
        fillColor: parseRgbValue(d.fill_color),
        fillOpacity: +d.fill_opacity,
      }
      : null;
  // fill color to use for the legend; not all layers get these as some aren't shown in the legend
  // and some have multiple values (e.g. color ramps)
  hash[d.datacode].legend_fill = d.fill_color ? parseRgbValue(d.fill_color) : null;
  // units to display in the legend, should be a comma separated list
  hash[d.datacode].legend_units = d.legend_units ? d.legend_units.split(',') : null;
  // if it's a marker, parse the lat lng coordinates
  hash[d.datacode].coords =
    d.datatype === 'marker' ? d.marker_lat_lng.split(',').map(e => +e) : null;
  // make sure layer list order is actually a number
  hash[d.datacode].listed_order = parseInt(d.listed_order, 10);
  // tile layer opacity value
  hash[d.datacode].tile_opacity = d.datatype !== 'geojson' && d.tile_opacity ? parseFloat(d.tile_opacity, 10) : null;

  return hash;
}, {});

// default Redux reducer state:
// - activeLayers (array of strings): a list of layer ids that have been selected by the user
// - basemap: (string): the current basemap
// - dataLayers (obj): the layer hash created above
// - isFetchingGeoJson (bool): are we requesting a GeoJSON file async?
// - error (obj || string || null): was there an error requesting a GeoJSON file?
// - zoomLayer (string): datacode of layer to zoom to
// - zoomHome (bool): should the map zoom home to original zoom setting
const defaultState = {
  activeLayers: DEFAULT_LAYERS,
  basemap: 'watercolor',
  dataLayers,
  isFetchingGeoJson: false,
  error: null,
  zoomLayer: '',
  zoomHome: false,
};

// When a user deselects all map layers we need to iterate over the layer hash and set selected to
// false. This happens when the reducer intercepts the DESELECT_ALL_MAP_LAYERS action
function deselectAllDataLayers(oldDataLayers) {
  const newDataLayers = { ...oldDataLayers };

  Object.keys(newDataLayers).forEach(key => {
    newDataLayers[key].selected = false;
  });

  return newDataLayers;
}

export default function(state = defaultState, action) {
  switch (action.type) {
    case SELECT_MAP_LAYER:
      // add the layer id to the activeLayers list
      // flip the selected flag of the layer to true
      return {
        ...state,
        activeLayers: [...state.activeLayers, action.layer],
        dataLayers: {
          ...state.dataLayers,
          [action.layer]: {
            ...state.dataLayers[action.layer],
            selected: true,
          },
        },
      };

    case DESELECT_MAP_LAYER:
      // remove the layer id from the activeLayers list
      // flip the selected flag of the layer to false
      return {
        ...state,
        activeLayers: state.activeLayers.filter(layer => action.layer !== layer),
        dataLayers: {
          ...state.dataLayers,
          [action.layer]: {
            ...state.dataLayers[action.layer],
            selected: false,
          },
        },
      };

    case DESELECT_ALL_MAP_LAYERS:
      return {
        ...state,
        activeLayers: [],
        dataLayers: deselectAllDataLayers(state.dataLayers),
      };

    case TOGGLE_BASEMAP:
      return {
        ...state,
        basemap: action.basemap,
      };

    case ZOOM_TO_MAP_HOME:
      return {
        ...state,
        zoomHome: !state.zoomHome,
      };

    case ZOOM_TO_MAP_LAYER_EXTENT:
      return {
        ...state,
        zoomLayer: action.layer,
      };

    case GEOJSON_LAYER_REQUEST:
      return {
        ...state,
        isFetchingGeoJson: true,
      };

    case GEOJSON_LAYER_SUCCESS:
      // on a successful GeoJSON request for a given vector layer
      // instantiate a Leaflet geoJson layer with the response GeoJSON
      return {
        ...state,
        isFetchingGeoJson: false,
        dataLayers: {
          ...state.dataLayers,
          [action.layer]: {
            ...state.dataLayers[action.layer],
            leaflet: L.geoJson(action.json),
          },
        },
      };

    case GEOJSON_LAYER_ERROR:
      return {
        ...state,
        error: action.error,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/mapLayersReducer.js